import { ConfusedButton } from './ConfusedButton';
import { FullscreenButton } from './FullscreenButton';
import { InstructorSentimentInfo } from './InstructorSentimentInfo';
import { PlayPauseButton } from './PlayPauseButton';
import { SeekBar } from './SeekBar';
import { SpeedSelector } from './SpeedSelector';
import { TimeDisplay } from './TimeDisplay';
import { VolumeControl } from './VolumeControl';
import type { PlaybackRate } from './player.types';
import type { SentimentOverlayData } from './sentimentTypes';
import styles from './ControlBar.module.css';

type SentimentStatus = 'error' | 'idle' | 'pending' | 'success';

interface ControlBarProps {
  canRecordConfusion: boolean;
  confusionDisabledReason?: string;
  controlsVisible: boolean;
  currentTime: number;
  duration: number;
  hasInstructorSentimentData?: boolean;
  isFullscreen: boolean;
  isMuted: boolean;
  isPlaying: boolean;
  onConfused: () => void;
  onFullscreenToggle: (trigger: HTMLElement | null) => void;
  onPlaybackRateChange: (rate: PlaybackRate) => void;
  onPlayPause: () => void;
  onSeek: (seconds: number) => void;
  onToggleMute: () => void;
  onVolumeChange: (volume: number) => void;
  playbackRate: PlaybackRate;
  sentimentOverlay?: SentimentOverlayData | null;
  sentimentStatus: SentimentStatus;
  showConfusedButton?: boolean;
  showInstructorSentimentInfo?: boolean;
  /** Why Play, Mute, speed and volume cannot be used yet, while the player loads. */
  unavailableReason?: string;
  volume: number;
}

export function ControlBar({
  canRecordConfusion,
  confusionDisabledReason,
  controlsVisible,
  currentTime,
  duration,
  hasInstructorSentimentData = false,
  isFullscreen,
  isMuted,
  isPlaying,
  onConfused,
  onFullscreenToggle,
  onPlaybackRateChange,
  onPlayPause,
  onSeek,
  onToggleMute,
  onVolumeChange,
  playbackRate,
  sentimentOverlay,
  sentimentStatus,
  showConfusedButton = true,
  showInstructorSentimentInfo = false,
  unavailableReason,
  volume,
}: ControlBarProps) {
  return (
    <div className={styles.bar} data-visible={controlsVisible}>
      <div className={styles.primaryRow}>
        <PlayPauseButton
          isPlaying={isPlaying}
          onToggle={onPlayPause}
          unavailableReason={unavailableReason}
        />
        <SeekBar
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
          sentimentOverlay={sentimentOverlay}
        />
        <TimeDisplay currentTime={currentTime} duration={duration} />
        <VolumeControl
          isMuted={isMuted}
          onToggleMute={onToggleMute}
          onVolumeChange={onVolumeChange}
          unavailableReason={unavailableReason}
          volume={volume}
        />
        <SpeedSelector
          onChange={onPlaybackRateChange}
          unavailableReason={unavailableReason}
          value={playbackRate}
        />
        <FullscreenButton isFullscreen={isFullscreen} onToggle={onFullscreenToggle} />
      </div>

      {showConfusedButton || (showInstructorSentimentInfo && hasInstructorSentimentData) ? (
        <div className={styles.secondaryRow}>
          {showConfusedButton ? (
            <ConfusedButton
              disabled={!canRecordConfusion}
              disabledReason={confusionDisabledReason}
              onRecord={onConfused}
              status={sentimentStatus}
            />
          ) : null}
          {showInstructorSentimentInfo && hasInstructorSentimentData ? (
            <InstructorSentimentInfo />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
